// External modules
import { updateAppleShippingContact } from '@paypalcorp/checkout-apple-pay';

// Internal modules
import { appStore } from '@/store';
import {
  features,
  getContent,
  getDonationMetadata,
  getLocale,
  getShippingAddress,
  getShippingCountry,
  getToken,
  logger,
} from '@/lib';
import { EXCLUDED_COUNTRIES } from '../constants';
import { redirectToGenericError } from '@/lib/utils/redirects';
import {
  logDonationEligibilityCheck,
  logDonationIneligibleError,
  logFPTIWhenDomesticTransactionBlocked,
} from '../fpti';
import { CountryCodes } from '@/generated/graphql';
import type { MerchantAddressConfiguration } from '@/components/Billing/types';

import type {
  Cart,
  CheckoutSession,
  Maybe,
  Merchant,
  Scalars,
  ShippingAndTaxCharges,
  ShippingOptionsSource,
} from '@/generated/graphql';
import { apolloClient } from '@/lib/apollo';
import {
  CALCULATE_SHIPPING_AND_TAXES_QUERY,
  PROFILE_SHIPPING_OPTIONS_QUERY,
} from '@/components/Shipping/graphql';
// TODO: this needs to be cleaned up once the xobuyer schema is updated to
// have ShippingAndTaxCharges.id field as a non-required field
export type ShippingAndTaxes = {
  __typename?: 'ShippingAndTaxes';
  /** List of shipping and tax charges */
  charges: Array<Partial<ShippingAndTaxCharges>>;
  /** Whether shipping is supported for this region */
  isShippingRegionSupported: Scalars['Boolean'];
  /** Whether tax is required for shipping */
  isTaxOnShippingRequired: Scalars['Boolean'];
  /** Whether profile override is allowed */
  profileOverrideAllowed?: Maybe<Scalars['Boolean']>;
  /** Shipping options source ('PROFILE' or 'CALLBACK' or 'FALLBACK') */
  shippingOptionsSource: ShippingOptionsSource;
  /** Whether shipping options are shown */
  shouldShowShippingOptions: Scalars['Boolean'];
};

/**
 * Checks donor donation eligibility and sets UI error state if ineligible.
 */
const handleDonationEligibilityError = (): void => {
  if (!features.isActive('isNonGncDonation')) {
    return;
  }

  const donationMetadata = getDonationMetadata();
  const { donationEligibility } = appStore.getState().api;

  logDonationEligibilityCheck(donationMetadata, donationEligibility);

  const isIneligible = donationEligibility?.isSenderEligible === false;

  appStore.getActions().ui.setHasDonationEligibilityError(isIneligible);

  if (isIneligible) {
    logDonationIneligibleError(donationMetadata, donationEligibility);
  }
};

/**
 * @function handleBlockedDomesticTransactions
 * @description Checks if a transaction is blocked based
 * on the merchant country and sets the UI error message.
 */
const handleBlockedDomesticTransactions = (): void => {
  let { country: merchantCountry } =
    appStore.getState().api.cartDetails.merchant;
  const { country: buyerCountry } = getLocale();
  const { ui: uiActions } = appStore.getActions();

  // Merchant country can sometimes come in as CN, we need to remap these to C2
  // since buyerCountry will always be C2
  merchantCountry =
    merchantCountry === CountryCodes.Cn ? CountryCodes.C2 : merchantCountry;

  if (
    features.isActive('shouldBlockDomesticTransactions') &&
    merchantCountry === buyerCountry
  ) {
    logFPTIWhenDomesticTransactionBlocked(merchantCountry);
    // Check if it's a blocked domestic transaction to handle it
    if (EXCLUDED_COUNTRIES.includes(merchantCountry)) {
      uiActions.setIsBlockedDomesticTransaction(true);
    }
  }
};

/**
 * @function handleSignupAndGuestIneligible
 * @description Redirect to a generic error when both signup and guest are
 *  ineligible. This should only happen if the buyer is from a country that
 *  disabled signup and if the merchant is enforcing signup (e.g. gambling)
 */
const handleSignupAndGuestIneligible = (): void => {
  const isSignupDisabled = features.isActive('shouldDisableSignup');
  const isOptionalSignup = features.isActive('isOptionalSignup');
  const isSignupDisabledForIndia = features.isActive(
    'shouldDisableSignupForIndia',
  );
  const { country } = getLocale();

  /**
   * Gibraltar is TEMPORARILY being ramped separately from the
   * main `shouldDisableSignup` feature flag. We should remove
   * references to this code once it is included in the default
   * allocation as part of ramp cleanup.
   * */
  const isSignupDisabledForGibraltar =
    features.isActive('shouldDisableSignupForGibraltar') &&
    country === CountryCodes.Gi;

  const isNonGncDonation = features.isActive('isNonGncDonation');

  if (
    !isNonGncDonation &&
    (isSignupDisabled ||
      isSignupDisabledForGibraltar ||
      isSignupDisabledForIndia) &&
    !isOptionalSignup
  ) {
    logger.error('weasley_error_signup_and_guest_ineligible', {
      buyerCountry: getLocale().country,
    });
    if (isSignupDisabledForIndia) {
      redirectToGenericError({ message: 'PAYEE_ACCOUNT_LOCKED_OR_CLOSED' });
    } else {
      redirectToGenericError();
    }
  }
};

const handleBuildApplePayMerchantConfig = ({
  cart = {},
  merchant = {},
  shippingAndTaxes,
}: {
  cart?: Maybe<Cart>;
  merchant?: Partial<Merchant>;
  shippingAndTaxes: Partial<ShippingAndTaxes> | undefined;
}): MerchantAddressConfiguration | undefined => {
  const { country } = getLocale();

  if (!cart?.shippingAddress || !shippingAndTaxes?.shippingOptionsSource) {
    return; // Will go with default instantiated merchant config.
  }

  const charges = shippingAndTaxes?.charges || [];
  const shouldShowShippingOptions =
    shippingAndTaxes?.shouldShowShippingOptions || false;

  const updateAppleSheet = updateAppleShippingContact(
    charges,
    cart?.amounts,
    `${merchant?.name} ${getContent('applepay.viapaypal')}`,
    shouldShowShippingOptions,
  );

  const merchantPassedShippingAddress = cart?.shippingAddress;

  const merchantPassedAddressLines = [];
  if (merchantPassedShippingAddress?.line1) {
    merchantPassedAddressLines.push(merchantPassedShippingAddress.line1);
  }
  if (merchantPassedShippingAddress?.line2) {
    merchantPassedAddressLines.push(merchantPassedShippingAddress.line2);
  }

  return {
    merchantShippingConfig: {
      charges,
      shippingOptionsSource: shippingAndTaxes.shippingOptionsSource,
    },
    paymentRequest: {
      lineItems: updateAppleSheet.newLineItems,
      shippingContact: {
        ...(merchantPassedAddressLines.length && {
          addressLines: merchantPassedAddressLines,
        }),
        ...(merchantPassedShippingAddress.state && {
          administrativeArea: merchantPassedShippingAddress.state,
        }),
        ...(country && { countryCode: country }),
        ...(merchantPassedShippingAddress.lastName && {
          familyName: merchantPassedShippingAddress.lastName,
        }),
        ...(merchantPassedShippingAddress.firstName && {
          givenName: merchantPassedShippingAddress.firstName,
        }),
        ...(merchantPassedShippingAddress.city && {
          locality: merchantPassedShippingAddress.city,
        }),
        ...(merchantPassedShippingAddress.postalCode && {
          postalCode: merchantPassedShippingAddress.postalCode,
        }),
      },
      shippingMethods: updateAppleSheet.newShippingMethods,
      total: updateAppleSheet.newTotal,
    },
  };
};

const getCachedCheckoutSession = (): {
  checkoutSession: Partial<CheckoutSession>;
} => {
  const cachedResponse = apolloClient.readQuery({
    query: PROFILE_SHIPPING_OPTIONS_QUERY,
    variables: {
      token: getToken(),
    },
  });

  return cachedResponse;
};

const getCachedShippingAndTax = (): {
  calculateShippingAndTaxes: Partial<ShippingAndTaxes>;
} => {
  const shippingAddress = getShippingAddress();
  const {
    line1: { value: line1 },
    line2: { value: line2 },
    postalCode: { value: postalCode },
    state: { value: state },
    city: { value: city },
  } = shippingAddress;

  const { language } = getLocale();
  const country = getShippingCountry();

  const variables = {
    locale: {
      country,
      lang: language,
    },
    shippingAddress: {
      city,
      country,
      line1,
      line2,
      postalCode,
      state,
    },
    token: getToken(),
  };

  const cachedResponse = apolloClient.readQuery({
    query: CALCULATE_SHIPPING_AND_TAXES_QUERY,
    variables,
  });

  return cachedResponse;
};

export {
  getCachedCheckoutSession,
  getCachedShippingAndTax,
  handleBlockedDomesticTransactions,
  handleBuildApplePayMerchantConfig,
  handleDonationEligibilityError,
  handleSignupAndGuestIneligible,
};
